import {
  HomepageContent,
  HomepageContentApiResponse,
  PriceApiProps,
  PriceResultProps,
  ReservationRequestProps,
} from "./type";
import { getRegionConfig, type RegionCode } from "./regionConfig";

const regionConfig = getRegionConfig();

export async function getBanners(type: string) {
  const res = await fetch(`${regionConfig.apiUrl}banners?type=${type}`);
  const json = await res.json();

  return json;
}

export async function getHomepageContent(
  fallback: HomepageContent
): Promise<HomepageContent> {
  try {
    const res = await fetch(`${regionConfig.apiUrl}homepage-sections`, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      return fallback;
    }

    const json: HomepageContentApiResponse = await res.json();
    const remote = json.data;
    const hasThreeFleetCards = remote.fleet.cards.length === 3;

    return {
      leasing: {
        eyebrow: remote.leasing.eyebrow || fallback.leasing.eyebrow,
        title: remote.leasing.title || fallback.leasing.title,
        description:
          remote.leasing.description || fallback.leasing.description,
        buttonText:
          remote.leasing.button_text || fallback.leasing.buttonText,
        buttonUrl: remote.leasing.button_url || fallback.leasing.buttonUrl,
        imageUrl: remote.leasing.image_url || fallback.leasing.imageUrl,
      },
      fleet: {
        title: remote.fleet.title || fallback.fleet.title,
        description:
          remote.fleet.description || fallback.fleet.description,
        cards: hasThreeFleetCards
          ? remote.fleet.cards.map((card, index) => ({
              title: card.title || fallback.fleet.cards[index].title,
              description:
                card.description || fallback.fleet.cards[index].description,
              buttonText:
                card.button_text || fallback.fleet.cards[index].buttonText,
              url: card.url || fallback.fleet.cards[index].url,
              imageUrl:
                card.image_url || fallback.fleet.cards[index].imageUrl,
            }))
          : fallback.fleet.cards,
      },
    };
  } catch (error) {
    console.warn("Homepage customization unavailable; using defaults.", error);
    return fallback;
  }
}

export async function getFeaturedStation() {
  const localPromise = fetch(`${regionConfig.apiUrl}stations/featured`)
    .then(r => r.json())
    .then(json => (json.success ? json.results : []))
    .catch(() => []);

  const otherPromise = regionConfig.otherRegionApiUrl
    ? fetch(`${regionConfig.otherRegionApiUrl}stations/featured`)
        .then(r => r.json())
        .then(json => (json.success ? json.results.map((s: any) => ({ ...s, _region: regionConfig.otherRegion.code })) : []))
        .catch(() => [])
    : Promise.resolve([]);

  const [local, other] = await Promise.all([localPromise, otherPromise]);
  return [...local, ...other];
}

export async function getAllStation() {
  const res = await fetch(`${regionConfig.apiUrl}stations`);
  const json = await res.json();

  return json;
}

export async function getStation(keyword: string) {
  const localPromise = fetch(
    `${regionConfig.apiUrl}stations/search?query=${keyword}`,
    { headers: { 'Accept': 'application/json' } }
  )
    .then(r => r.json())
    .then(json => (json.success ? json.results : []))
    .catch(() => []);

  const otherPromise = regionConfig.otherRegionApiUrl
    ? fetch(
        `${regionConfig.otherRegionApiUrl}stations/search?query=${keyword}`,
        { headers: { 'Accept': 'application/json' } }
      )
        .then(r => r.json())
        .then(json => (json.success ? json.results.map((s: any) => ({ ...s, _region: regionConfig.otherRegion.code })) : []))
        .catch(() => [])
    : Promise.resolve([]);

  const [local, other] = await Promise.all([localPromise, otherPromise]);
  return [...local, ...other];
}

async function fetchPriceFromApi(
  apiUrl: string,
  regionCode: RegionCode,
  params: PriceApiProps
): Promise<PriceResultProps[]> {
  const {
    countryId,
    pickUpDateTime,
    pickUpRentalStation,
    dropOffUpDateTime,
    dropOffRentalStation,
    driverAge,
    countryOfResidence,
  } = params;

  const res = await fetch(
    `${apiUrl}price?countryId=${countryId}&pickUpDateTime=${pickUpDateTime}&pickUpRentalStation=${pickUpRentalStation}&dropOffUpDateTime=${dropOffUpDateTime}&dropOffRentalStation=${dropOffRentalStation}${driverAge ? `&driverAge=${driverAge}` : ''}${countryOfResidence ? `&countryOfResidence=${countryOfResidence}` : ''}`
    ,{    headers: {
      'Accept': 'application/json',
    }}
  );
  const json = await res.json();

  // The price API signals problems with different keys: `message` (no vehicles /
  // inventory / maintenance) and `error` (upstream 404 / unexpected server error).
  // Surface either as a throwable, human-readable message.
  if (json && !Array.isArray(json) && (json.message || json.error)) {
    throw Error(json.message || json.error);
  }

  // Contract guard: a successful price response is an array of vehicles. If the
  // API ever returns another shape (e.g. a transient error object without a
  // message), fail with a clear message instead of crashing on `.map`.
  if (!Array.isArray(json)) {
    throw Error("We couldn't load availability right now. Please try again with another date, time or location.");
  }

  // Tag each result with region and apiUrl
  const results: PriceResultProps[] = json;
  return results.map((item) => ({
    ...item,
    _region: regionCode,
    _apiUrl: apiUrl,
  }));
}

export async function getPrice({
  countryId,
  pickUpDateTime,
  pickUpRentalStation,
  dropOffUpDateTime,
  dropOffRentalStation,
  driverAge,
  countryOfResidence,
}: PriceApiProps) {
  const params: PriceApiProps = {
    countryId,
    pickUpDateTime,
    pickUpRentalStation,
    dropOffUpDateTime,
    dropOffRentalStation,
    driverAge,
    countryOfResidence,
  };

  // Local region fetch (catch errors to allow cross-region redirect)
  const localPromise = fetchPriceFromApi(regionConfig.apiUrl, regionConfig.code, params)
    .catch(() => [] as PriceResultProps[]);

  // Other region fetch (parallel, errors silently caught)
  const otherRegionConfig = regionConfig.otherRegionApiUrl
    ? fetchPriceFromApi(
        regionConfig.otherRegionApiUrl,
        regionConfig.otherRegion.code,
        {
          ...params,
          countryId: regionConfig.otherRegion.code === "MY" ? "81" : "127",
        }
      ).catch((err) => {
        console.warn("Other region price fetch failed (non-critical):", err.message);
        return [] as PriceResultProps[];
      })
    : Promise.resolve([] as PriceResultProps[]);

  const [localResults, otherResults] = await Promise.all([localPromise, otherRegionConfig]);

  // Track "no result" for local region in GA
  if (localResults.length === 0) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'no_vehicles_found', {
          event_category: 'search_errors',
          event_label: 'no_results',
          pickup_station: pickUpRentalStation,
          dropoff_station: dropOffRentalStation,
          pickup_datetime: pickUpDateTime,
          dropoff_datetime: dropOffUpDateTime,
        });
      } catch (error) {
        console.error('Error sending GA event:', error);
      }
    }
  }

  // Combine: local results first, then other region results
  return [...localResults, ...otherResults];
}

export async function postReservations(bodyData: ReservationRequestProps, apiUrl?: string) {
  const targetApiUrl = apiUrl || regionConfig.apiUrl;
  const res = await fetch(`${targetApiUrl}reservations`, {
    headers: { "Content-Type": "application/json" },
    method: "post",
    body: JSON.stringify(bodyData),
  });
  const json = await res.json();

  return json;
}

export async function validateDiscountCode(code: string, totalAmount: number, apiUrl?: string) {
  const targetApiUrl = apiUrl || regionConfig.apiUrl;
  const response = await fetch(`${targetApiUrl}discount-codes/validate/${code}?total_amount=${totalAmount}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.json();
}
